import { PageTransition } from '@/constant/transition'
import { range } from '@/util'
import {
  fetchAreaPrefecturesMap,
  fetchSchools,
  type AreaPrefectureMapping,
  type School,
} from '@/util/ref'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { AnimatePresence, motion } from 'framer-motion'
import { useAtom } from 'jotai'
import Button from 'react-bootstrap/Button'
import FloatingLabel from 'react-bootstrap/FloatingLabel'
import Form from 'react-bootstrap/Form'
import Stack from 'react-bootstrap/Stack'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { selectedProgramType } from '../ProgramTypeSelection/data'
import { calGrade } from '@/util'
import { DEFAULT_EDU_INFO, EduInfoSchema, fetchFirstEducationHistory } from './data'
import { container } from './style.css'
import { syncEducationHistory, type EducationHistory } from '../helper'
import { useEffect, useMemo } from 'react'
import { ProgressFlags } from '@/util/progress'
import { syncProgress } from '../helper'
import { useWatch } from 'react-hook-form'
import { queryClient } from '@/util/queryClient'

const DEFAULT_DURATION: Record<string, number> = {
  KOSEN_REGULAR: 5,
  KOSEN_ADVANCED: 2,
}

function getDefaultDuration(programType: string) {
  return DEFAULT_DURATION[programType] || 0
}

export default function EducationInfo() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    data: areaPrefectureMapping,
    isLoading: areaPrefectureLoading,
    isError: areaPrefectureError,
  } = useQuery<AreaPrefectureMapping>({
    queryKey: ['area-prefecture'],
    queryFn: fetchAreaPrefecturesMap,
    staleTime: Infinity,
  })
  const {
    data: schoolProgramList,
    isLoading: schoolProgramLoading,
    isError: schoolProgramError,
  } = useQuery<School[]>({
    queryKey: ['school'],
    queryFn: fetchSchools,
    staleTime: 1000 * 60 * 5,
  })
  const { data: edu } = useQuery({
    queryKey: ['education-history', 0],
    queryFn: fetchFirstEducationHistory,
    staleTime: 1000 * 60 * 60,
  })

  // 本科 OR 専攻科
  const [{ programType }] = useAtom(selectedProgramType)

  const calculatedCourses = useMemo(() => {
    if (!edu?.school?.id || !edu?.academicProgram?.id || !schoolProgramList) return []
    return (
      schoolProgramList
        ?.find((sch) => sch.id === edu.school?.id)
        ?.programs.find((p) => p.id === edu.academicProgram?.id)?.courses || []
    )
  }, [edu, schoolProgramList])

  const {
    setValue,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(EduInfoSchema),
    values: edu
      ? {
          region: edu.school?.subnationalUnit ?? DEFAULT_EDU_INFO.region,
          prefecture: edu.school?.adminDivision ?? DEFAULT_EDU_INFO.prefecture,
          schoolId: edu.school?.id ?? DEFAULT_EDU_INFO.schoolId,
          academicProgramId: edu.academicProgram?.id ?? DEFAULT_EDU_INFO.academicProgramId,
          academicCourseId: calculatedCourses.length
            ? (edu.academicCourse?.id ?? DEFAULT_EDU_INFO.academicCourseId)
            : 0,
          grade: calGrade(edu.enrollmentStartDate) ?? DEFAULT_EDU_INFO.grade,
        }
      : DEFAULT_EDU_INFO,
  })

  const eduHistoryMutation = useMutation({
    mutationFn: (edu: EducationHistory) =>
      syncEducationHistory({
        ...edu,
        academicProgramId: edu.academicProgramId || undefined,
        academicCourseId: edu.academicCourseId || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['education-history'] })
      syncProgress(ProgressFlags.EDU_HISTORY_FILLED)
      navigate('/onboarding/basic-job-application-info', {
        state: { direction: PageTransition.FromRight },
      })
    },
    onError: (err) => console.log(err),
  })

  const [region, prefecture, schoolId, academicProgramId] = useWatch({
    control,
    name: ['region', 'prefecture', 'schoolId', 'academicProgramId'],
  })

  const courses =
    region && prefecture && schoolId && academicProgramId
      ? schoolProgramList
          ?.find((sch) => sch.id === schoolId)
          ?.programs.find((p) => p.id === academicProgramId)?.courses || []
      : []

  const durationYears =
    schoolProgramList
      ?.find((s) => s.id === schoolId)
      ?.programs.find((p) => p.id === academicProgramId)?.durationYears ||
    getDefaultDuration(programType)

  useEffect(() => {
    if (!programType) {
      navigate('/onboarding/program-type-selection')
    }
  }, [programType, navigate])

  return (
    <div className={container}>
      <h1>あなたの高専はどちらですか？</h1>
      <p className="text-muted small">まずはお住まいの地域を選択してください</p>
      <FloatingLabel controlId="area-selector" label="地方" className="mb-3">
        <Controller
          name="region"
          control={control}
          render={({ field }) => (
            <Form.Select
              {...field}
              onChange={(e) => {
                field.onChange(e)
                setValue('prefecture', DEFAULT_EDU_INFO.prefecture)
                setValue('schoolId', DEFAULT_EDU_INFO.schoolId)
                setValue('academicProgramId', DEFAULT_EDU_INFO.academicProgramId)
                setValue(
                  'academicCourseId',
                  courses?.length && academicProgramId !== 0 ? DEFAULT_EDU_INFO.academicCourseId : 0
                )
              }}
            >
              <option value="" disabled hidden>
                地方を選択してください
              </option>
              {areaPrefectureError ? (
                <option>error</option>
              ) : areaPrefectureLoading || !areaPrefectureMapping ? (
                <option>Loading...</option>
              ) : (
                Object.keys(areaPrefectureMapping.areaToPrefecture).map((k) => (
                  <option key={k} value={k}>
                    {t(`region.${k}`)}
                  </option>
                ))
              )}
            </Form.Select>
          )}
        />
      </FloatingLabel>
      <FloatingLabel controlId="prefecture-selector" label="都道府県" className="mb-3">
        <Controller
          name="prefecture"
          control={control}
          render={({ field }) => (
            <Form.Select
              {...field}
              aria-label="Floating label prefecture selector"
              onChange={(e) => {
                field.onChange(e)
                const region = areaPrefectureMapping?.prefectureToArea?.[e.target.value]
                if (region) setValue('region', region)
                setValue('schoolId', DEFAULT_EDU_INFO.schoolId)
                setValue('academicProgramId', DEFAULT_EDU_INFO.academicProgramId)
                setValue(
                  'academicCourseId',
                  courses?.length && academicProgramId !== 0 ? DEFAULT_EDU_INFO.academicCourseId : 0
                )
              }}
            >
              <option value="" disabled hidden>
                都道府県を選択してください
              </option>
              {areaPrefectureError ? (
                <option>error</option>
              ) : areaPrefectureLoading ? (
                <option>Loading...</option>
              ) : (
                (
                  areaPrefectureMapping?.areaToPrefecture?.[region || ''] ??
                  Object.keys(areaPrefectureMapping?.prefectureToArea ?? {})
                ).map((k) => (
                  <option key={k} value={k}>
                    {t(`prefecture.${k}`)}
                  </option>
                ))
              )}
            </Form.Select>
          )}
        />
      </FloatingLabel>
      <FloatingLabel controlId="school-selector" label="学校" className="mb-3">
        <Controller
          name="schoolId"
          control={control}
          render={({ field }) => (
            <Form.Select
              {...field}
              onChange={({ target: { value: schoolId } }) => {
                field.onChange(Number(schoolId))
                setValue('academicProgramId', DEFAULT_EDU_INFO.academicProgramId)
                setValue(
                  'academicCourseId',
                  courses?.length && academicProgramId !== 0 ? DEFAULT_EDU_INFO.academicCourseId : 0
                )
                for (const school of schoolProgramList || []) {
                  if (school.id.toString() === schoolId) {
                    setValue('region', school.subnationalUnit)
                    setValue('prefecture', school.adminDivision)
                  }
                }
              }}
            >
              <option value={DEFAULT_EDU_INFO.schoolId} disabled hidden>
                学校を選択してください
              </option>
              {schoolProgramError ? (
                <option>error</option>
              ) : schoolProgramLoading || !schoolProgramList ? (
                <option>Loading...</option>
              ) : (
                schoolProgramList
                  .filter(
                    (k) =>
                      (!region || k.subnationalUnit === region) &&
                      (!prefecture || k.adminDivision === prefecture) &&
                      k.programs.map((p) => p.programType).includes(programType)
                  )
                  .map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name}
                    </option>
                  ))
              )}
            </Form.Select>
          )}
        />
      </FloatingLabel>
      <FloatingLabel controlId="program-selector" label="学科" className="mb-3">
        <Controller
          name="academicProgramId"
          control={control}
          render={({ field }) => (
            <Form.Select
              {...field}
              onChange={({ target: { value } }) => {
                const academicProgramId = Number(value)
                field.onChange(academicProgramId)
                setValue(
                  'academicCourseId',
                  courses?.length && academicProgramId !== 0 ? DEFAULT_EDU_INFO.academicCourseId : 0
                )
              }}
            >
              <option value={DEFAULT_EDU_INFO.academicProgramId} disabled hidden>
                学科選択してください
              </option>
              {schoolProgramError ? (
                <option>error</option>
              ) : schoolProgramLoading || !schoolProgramList ? (
                <option>Loading...</option>
              ) : (
                schoolProgramList
                  ?.find((s) => s.id === schoolId)
                  ?.programs.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))
              )}
              <option value={0}>その他</option>
            </Form.Select>
          )}
        />
      </FloatingLabel>
      <AnimatePresence mode="wait">
        {courses.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -100 }}
          >
            <FloatingLabel controlId="course-selector" label="コース" className="mb-3">
              <Controller
                name="academicCourseId"
                control={control}
                render={({ field }) => (
                  <Form.Select
                    {...field}
                    onChange={({ target: { value: academicCourseId } }) => {
                      field.onChange(Number(academicCourseId))
                    }}
                    aria-label="Floating label program selector"
                  >
                    <option value={DEFAULT_EDU_INFO.academicCourseId} disabled hidden>
                      コース選択してください
                    </option>
                    {courses.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                    <option value={0}>専攻なし</option>
                  </Form.Select>
                )}
              />
            </FloatingLabel>
          </motion.div>
        )}
      </AnimatePresence>
      <FloatingLabel controlId="grade-selector" label="学年" className="mb-3">
        <Controller
          name="grade"
          control={control}
          render={({ field }) => (
            <Form.Select
              {...field}
              onChange={({ target: { value: grade } }) => {
                field.onChange(Number(grade))
              }}
              aria-label="Floating label grade selector"
            >
              <option value={DEFAULT_EDU_INFO.grade} disabled hidden>
                学年選択してください
              </option>
              {schoolProgramError ? (
                <option>error</option>
              ) : schoolProgramLoading || !schoolProgramList ? (
                <option>Loading...</option>
              ) : (
                Array.from(range(1, durationYears + 1)).map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))
              )}
            </Form.Select>
          )}
        />
      </FloatingLabel>
      <Stack direction="horizontal">
        <Button
          variant="outline-secondary"
          size="lg"
          onClick={() =>
            navigate('/onboarding/program-type-selection', {
              state: { direction: PageTransition.FromLeft },
            })
          }
        >
          ←
        </Button>
        <Button
          variant="outline-primary"
          size="lg"
          className="ms-auto"
          disabled={!!Object.keys(errors).length}
          onClick={handleSubmit(
            (data) => eduHistoryMutation.mutate({ ...data, programType }),
            (err) => console.log(err)
          )}
        >
          →
        </Button>
      </Stack>
    </div>
  )
}
